import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, Download, ShoppingCart } from '@/components/icons';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { Button } from '@/components/ui/Button';
import { FilterBar, FilterField } from '@/components/ui/FilterBar';
import { PeriodPicker, REPORT_UNITS, usePeriod } from '@/components/ui/PeriodPicker';
import { Table } from '@/components/ui/Table';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/form/Input';
import { Select } from '@/components/form/Select';
import { useDocViewer } from '@/components/ui/useDocViewer';
import { listOutwardGrns, outwardGrnExportPath, outwardGrnPhotos, type OutwardGrn } from '@/api/outwardGrn';
import { downloadExcel } from '@/lib/downloadExcel';
import { useToast } from '@/components/ui/useToast';
import { apiError } from '@/lib/apiError';
import { formatDate } from '@/lib/format';
import { OutwardGrnModal } from './OutwardGrnModal';
import { WeightHoldsPanel } from './WeightHoldsPanel';
import { orderRef, trackByOrderId } from '@/lib/orderRef';

const CHANNELS: [string, string][] = [['', 'All'], ['online', 'Online'], ['regular', 'Regular']];

export function OutwardGrnPage() {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [channel, setChannel] = useState('');
  const [open, setOpen] = useState(false);
  const doc = useDocViewer();
  const toast = useToast();

  // Dispatch note date — All / Date (from–to) / Month / Quarter / Year.
  const period = usePeriod();
  const { range } = period;
  const filter = useMemo(
    () => ({ status: status || undefined, channel: channel || undefined, from: range?.from, to: range?.to }),
    [status, channel, range],
  );

  // Export every matching Dispatch Note (server-side, no cap).
  function onExport() {
    downloadExcel(outwardGrnExportPath({ status: status || undefined, from: range?.from, to: range?.to, search: search.trim() || undefined }), 'GRHAMOY_Dispatch_Notes.xlsx')
      .catch((err) => toast.push(apiError(err, 'Export failed'), 'error'));
  }

  const query = useQuery({
    queryKey: ['outward-grn', filter],
    queryFn: () => listOutwardGrns(filter),
    refetchInterval: 60_000,
  });
  const data = query.data ?? [];

  return (
    <div>
      <SectionHeader
        title="Outward GRN — Dispatch Note"      />

      <WeightHoldsPanel />

      <div className="mb-3 flex gap-1">
        {CHANNELS.map(([v, label]) => (
          <button
            key={v}
            onClick={() => setChannel(v)}
            className={`rounded-md px-2.5 py-1 text-xs font-semibold transition-colors sm:px-3 sm:py-1.5 sm:text-sm ${channel === v ? 'bg-accent text-white' : 'bg-[var(--bg-secondary)] text-[var(--text-muted)] hover:text-[var(--text-primary)]'}`}
          >
            {label === 'Online' ? <><ShoppingCart size={12} className="mr-1 inline-block align-[-2px]" /> Online</> : label}
          </button>
        ))}
      </div>

      <FilterBar>
        <FilterField label="Search" className="col-span-2 sm:col-auto">
          <div className="relative w-full">
            <Search
              size={11}
              className="pointer-events-none absolute left-1.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)] sm:left-2.5"
              strokeWidth={2.25}
            />
            <Input
              placeholder="Search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="!pl-5 sm:!pl-8"
              aria-label="Search outward GRNs"
            />
          </div>
        </FilterField>
        <FilterField label="Status">
          <Select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All</option>
            <option>Issued</option>
            <option>Cancelled</option>
          </Select>
        </FilterField>
        <FilterField label="Period" className="col-span-3 sm:col-auto">
          <PeriodPicker {...period.picker} units={REPORT_UNITS} className="justify-start" />
        </FilterField>
        {/* Chip lives inside the filter block on both viewports.
            Mobile: full-width row below the fields (col-span-3) so the long
            label fits. Desktop: right-pinned via ml-auto. */}
        <button
          type="button"
          onClick={onExport}
          className="col-span-1 inline-flex h-7 items-center justify-center gap-1 rounded-lg border border-[var(--border-color)] bg-[var(--card-bg)] px-2 text-[11px] font-bold text-[var(--text-secondary)] shadow-sm transition hover:border-accent hover:text-accent sm:col-span-1 sm:ml-auto sm:h-8 sm:self-end sm:px-3 sm:text-xs"
          title="Export all dispatch notes to Excel"
        >
          <Download size={12} /> Export
        </button>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="col-span-2 inline-flex h-7 items-center justify-center gap-1 rounded-lg bg-accent px-2 text-[11px] font-bold text-white shadow-sm transition hover:brightness-110 sm:col-span-1 sm:h-8 sm:self-end sm:px-3 sm:text-xs"
        >
          ＋ Raise Outward GRN
        </button>
      </FilterBar>

      <Table<OutwardGrn>
        storageKey="outward-grn"
        paginated
        title="Dispatch Notes"
        count={`${data.length} records`}
        rows={data}
        search={search}
        rowKey={(r) => r.id}
        loading={query.isFetching && data.length === 0}
        columns={trackByOrderId(channel === 'online', { key: 'orderId', header: 'Order ID', cell: (r) => <span className="font-extrabold text-accent">{orderRef(r.dispatchOrder?.enquiry)}</span> }, ['grnNo','do','enq','pack'], [
          {
            key: 'grnNo',
            header: 'GRN-OUT #',
            cell: (r) => <span className="font-extrabold text-accent">{r.grnNo}</span>,
          },
          { key: 'date', header: 'Date', cell: (r) => formatDate(r.grnDate) },
          { key: 'do', header: 'DO Ref', cell: (r) => r.dispatchOrder?.doNo ?? '—' },
          { key: 'enq', header: 'Order Ref', cell: (r) => orderRef(r.dispatchOrder?.enquiry) },
          { key: 'channel', header: 'Channel', align: 'center', cell: (r) => (r.dispatchOrder?.enquiry?.source === 'Shopify' ? <Badge tone="info"><ShoppingCart size={12} className="mr-1 inline-block align-[-2px]" /> Online</Badge> : <span className="text-[var(--text-muted)]">Regular</span>) },
          { key: 'party', header: 'Customer', cell: (r) => <span className="font-bold">{r.onlineCustomer ? `${r.onlineCustomer} (Shopify)` : r.party?.name ?? '—'}</span> },
          { key: 'pack', header: 'Pack Ref', cell: (r) => r.packing?.packNo ?? '—' },
          { key: 'vehicle', header: 'Vehicle', cell: (r) => r.vehicleNo ?? '—' },
          { key: 'lr', header: 'LR #', cell: (r) => r.lrNo ?? '—' },
          { key: 'eway', header: 'E-way #', cell: (r) => r.ewayBillNo ?? '—' },
          { key: 'boxes', header: 'Boxes', align: 'right', cell: (r) => r.boxCount },
          {
            key: 'photos', header: 'Photos',
            // Dispatch photos — click a thumbnail to open it full size.
            cell: (r) => {
              const ph = outwardGrnPhotos(r);
              if (!ph.length) return '—';
              return (
                <div className="flex gap-1">
                  {ph.map((u, i) => (
                    <a key={u} href={u} target="_blank" rel="noreferrer" title={`Photo ${i + 1} — open full size`}>
                      <img src={u} alt={`Dispatch photo ${i + 1}`} className="h-8 w-8 rounded border border-[var(--border-color)] object-cover" />
                    </a>
                  ))}
                </div>
              );
            },
          },
          {
            key: 'invoice',
            header: 'Invoice',
            cell: (r) =>
              r.salesInvoices && r.salesInvoices.length > 0 ? (
                <Badge tone="success">{r.salesInvoices[0].invoiceNo}</Badge>
              ) : (
                <Badge tone="muted">Pending</Badge>
              ),
          },
          {
            key: 'status',
            header: 'Status',
            cell: (r) => (
              <Badge tone={r.status === 'Issued' ? 'success' : 'muted'}>
                {r.status}
                {r.isPartial ? ' · Partial' : ''}
              </Badge>
            ),
          },
          {
            key: 'actions',
            header: '',
            width: '190px',
            cell: (r) => (
              <div className="flex gap-1.5">
                <Button
                  variant="glass"
                  size="sm"
                  onClick={() => doc.open('grn-out', r.id, r.dispatchOrder?.enquiry?.source === 'Shopify' ? orderRef(r.dispatchOrder.enquiry) : r.grnNo)}
                >
                  View Doc
                </Button>
              </div>
            ),
          },
        ])}
      />

      <OutwardGrnModal open={open} onClose={() => setOpen(false)} />
    </div>
  );
}
